import { Condition, DiagnosticReport, Immunization, Observation } from "@medplum/fhirtypes";
import { Smart } from "@metriport/fhir-sdk";
import type { EhrWritePath } from "../../../../domain/ehr";
import { MedicationWithRefs } from "../table-transforms/medication-types";
import {
  ConditionSeries,
  ImmunizationSeries,
  MedicationSeries,
  ObservationSeries,
} from "../table-transforms/series-utils";
import {
  isAllergyIntolerance,
  isConditionSeries,
  isDiagnosticReport,
  isImmunizationSeries,
  isMedicationSeries,
  isObservationSeries,
  isProcedure,
} from "./type-guards";

/**
 * WRITE-BACK TRANSFORMER to support V1 write-back shape
 *
 * This module handles conversion from V2 data structures (ObservationSeries, etc.)
 * to V1 data structures for EHR write-back.
 *
 * WHY: V1 EHR write-back expects specific shapes:
 * - Vitals: GroupedVitals (with mostRecentObservation + sortedPoints)
 * - Labs: Raw Observation FHIR resource
 * - Medications: MedicationWithRefs (from EnhancedMedicationSeries)
 * - Notes: NoteData (decoded encounterText + date from DiagnosticReport)
 * - Procedures: Raw Procedure (pass-through)
 * - Allergies: Raw AllergyIntolerance (pass-through)
 * V2 uses unified Series structures that need to be transformed.
 */

// ============================================================================
// V1 TYPES (what EHR write-back expects)
// ============================================================================

type DataPoint = {
  value: number;
  date: string;
  unit?: string;
  bp?: { systolic: number; diastolic: number };
  id?: string;
  filename?: string;
};

type GroupedVitals = {
  title: string;
  mostRecentObservation: Observation;
  sortedPoints?: DataPoint[];
};

type GroupedConditions = {
  title: string;
  mostRecentCondition: Condition;
  // V1 conditions might have different structure
};

type GroupedImmunizations = {
  title: string;
  mostRecentImmunization: Immunization;
  // V1 immunizations might have different structure
};

type GroupedMedications = MedicationWithRefs;

type NoteData = {
  encounterText: string;
  date: string;
};

type EnhancedMedicationSeries = {
  id: string;
  code: string;
  codeDisplay: string;
  resourceType: string;
  events: unknown[];
  latestDate: string;
  earliestDate: string;
  medicationWithRefs: MedicationWithRefs;
};

// ============================================================================
// TYPE GUARDS - Detect V2 structures (imported from ./type-guards.ts)
// ============================================================================

function isEnhancedMedicationSeries(data: unknown): data is EnhancedMedicationSeries {
  return (
    typeof data === "object" &&
    data !== null &&
    "events" in data &&
    "medicationWithRefs" in data &&
    "codeDisplay" in data &&
    "code" in data &&
    "resourceType" in data &&
    (data as EnhancedMedicationSeries).resourceType === "Medication"
  );
}

// Raw FHIR resource type guards (isDiagnosticReport, isProcedure, isAllergyIntolerance)
// are imported from ./type-guards.ts

// ============================================================================
// CONVERTERS - V2 → V1
// ============================================================================

function convertObservationSeriesToGroupedVitals(series: ObservationSeries): GroupedVitals {
  const mostRecentObservation = series.resources[series.resources.length - 1];
  if (!mostRecentObservation) {
    throw new Error("ObservationSeries must have at least one resource");
  }
  const sortedPoints: DataPoint[] = series.resources
    .filter(resource => !!resource.valueQuantity?.value)
    .map(resource => ({
      value: resource.valueQuantity?.value as number,
      date: resource.effectiveDateTime ?? resource.effectivePeriod?.start ?? "",
      unit: resource.valueQuantity?.unit?.replace(/[{()}]/g, ""),
      id: resource.id,
      filename: resource.extension?.find(ext => ext.valueString)?.valueString,
    }));
  return {
    title: series.codeDisplay,
    mostRecentObservation: mostRecentObservation as Observation,
    sortedPoints,
  };
}

function convertObservationSeriesToRawObservation(series: ObservationSeries): Observation {
  // For labs, backend expects raw Observation, not GroupedLabs wrapper
  const mostRecentObservation = series.resources[series.resources.length - 1];
  if (!mostRecentObservation) {
    throw new Error("ObservationSeries must have at least one resource");
  }
  return mostRecentObservation as Observation;
}

function convertConditionSeriesToGroupedConditions(series: ConditionSeries): GroupedConditions {
  const mostRecentCondition = series.resources[series.resources.length - 1];
  if (!mostRecentCondition) {
    throw new Error("ConditionSeries must have at least one resource");
  }
  return {
    title: series.codeDisplay,
    mostRecentCondition: mostRecentCondition as Condition,
  };
}

function convertImmunizationSeriesToGroupedImmunizations(
  series: ImmunizationSeries
): GroupedImmunizations {
  const mostRecentImmunization = series.resources[series.resources.length - 1];
  if (!mostRecentImmunization) {
    throw new Error("ImmunizationSeries must have at least one resource");
  }
  return {
    title: series.codeDisplay,
    mostRecentImmunization: mostRecentImmunization as Immunization,
  };
}

function convertMedicationSeriesToMedicationWithRefs(series: MedicationSeries): GroupedMedications {
  // MedicationSeries.resources is already MedicationWithRefs[]
  // Just return the most recent one
  const mostRecent = series.resources[series.resources.length - 1];
  if (!mostRecent) {
    throw new Error("MedicationSeries must have at least one resource");
  }
  return mostRecent;
}

function convertEnhancedMedicationSeriesToMedicationWithRefs(
  series: EnhancedMedicationSeries
): GroupedMedications {
  // EnhancedMedicationSeries already has medicationWithRefs at the top level
  return series.medicationWithRefs;
}

function convertDiagnosticReportToNoteData(report: Smart<DiagnosticReport>): NoteData {
  // Extract and decode text from presentedForm
  let encounterText = "";

  if (report.presentedForm && report.presentedForm.length > 0) {
    const texts: string[] = [];

    for (const form of report.presentedForm) {
      if (!form.data) continue;

      try {
        // Decode base64 data
        const decoded = atob(form.data);
        texts.push(decoded);
      } catch (error) {
        console.error("Failed to decode presentedForm data:", error);
      }
    }

    encounterText = texts.join("\n");
  }

  // Fallback to conclusion if no presentedForm data
  if (!encounterText) {
    encounterText = report.conclusion || "";
  }

  // Extract date
  const date = report.effectiveDateTime ?? report.effectivePeriod?.start ?? report.issued ?? "";

  return {
    encounterText,
    date,
  };
}

// ============================================================================
// MAIN TRANSFORMER - Route by path
// ============================================================================

/**
 * Converts V2 data structures to V1 format for EHR write-back.
 *
 * @param originalData - The V2 data structure (ObservationSeries, ConditionSeries, etc.)
 * @param path - The EHR write path (determines which converter to use)
 * @returns V1-compatible data structure, or original data if already V1 or unknown
 */
export function transformV2ToV1WriteBackFormat(originalData: unknown, path: EhrWritePath): object {
  if (!isV2Format(originalData)) return originalData as object;
  switch (path) {
    case "vitals":
      if (isObservationSeries(originalData)) {
        return convertObservationSeriesToGroupedVitals(originalData);
      }
      break;
    case "lab":
      if (isObservationSeries(originalData)) {
        return convertObservationSeriesToRawObservation(originalData);
      }
      break;
    case "condition":
      if (isConditionSeries(originalData)) {
        return convertConditionSeriesToGroupedConditions(originalData);
      }
      break;
    case "immunization":
      if (isImmunizationSeries(originalData)) {
        return convertImmunizationSeriesToGroupedImmunizations(originalData);
      }
      break;
    case "medication":
      if (isEnhancedMedicationSeries(originalData)) {
        return convertEnhancedMedicationSeriesToMedicationWithRefs(originalData);
      }
      if (isMedicationSeries(originalData)) {
        return convertMedicationSeriesToMedicationWithRefs(originalData);
      }
      break;
    case "procedure":
      // V2 uses Smart<Procedure> which is already compatible with V1
      if (isProcedure(originalData)) return originalData as object;
      break;
    case "allergy":
      // V2 uses Smart<AllergyIntolerance> which is already compatible with V1
      if (isAllergyIntolerance(originalData)) return originalData as object;
      break;
    case "note":
      // V2 uses Smart<DiagnosticReport>, convert to NoteData for backend
      if (isDiagnosticReport(originalData)) {
        return convertDiagnosticReportToNoteData(originalData);
      }
      break;
    default:
      console.warn(`No legacy transformer for path: ${path}, passing through original data`);
  }
  // Fallback: return original if no converter matches
  return originalData as object;
}

/**
 * Helper to detect if data is V2 format (any Series type or raw FHIR resource)
 */
function isV2Format(data: unknown): boolean {
  return (
    isObservationSeries(data) ||
    isConditionSeries(data) ||
    isImmunizationSeries(data) ||
    isMedicationSeries(data) ||
    isEnhancedMedicationSeries(data) ||
    isDiagnosticReport(data) ||
    isProcedure(data) ||
    isAllergyIntolerance(data)
  );
}
